export function useRating() {
  const MIN_RATING = 1
  const MAX_RATING = 5

  /** A fast (non-cryptographic) hash function. */
  function fastHash(str: string) {
    let h1 = 0xDEADBEEF ^ 0
    let h2 = 0x41C6CE57 ^ 0

    for (let i = 0; i < str.length; i++) {
      const ch = str.charCodeAt(i)
      h1 = Math.imul(h1 ^ ch, 2654435761)
      h2 = Math.imul(h2 ^ ch, 1597334677)
    }

    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)

    return (h1 >>> 0) + 4294967296 * (h2 >>> 0)
  }

  /** A lightweight pseudo-random number generator. */
  class FastRandom {
    private state: number

    constructor(seed: number) {
      this.state = seed >>> 0
    }

    next(): number {
      this.state = (1664525 * this.state + 1013904223) >>> 0
      return this.state / 4294967296
    }
  }

  // Types for our rating function.
  type RatingRange = [number, number] | [number]
  type RatingPrecision = 0 | 1 | 2

  /**
   * Returns a rating based on a string value.
   * The same value will always return the same rating since it uses a deterministic PRNG seed.
   *
   * @param value The input string
   * @param precision The number of decimal places (defaults to 1)
   * @param range The [min, max] rating range (defaults to [1, 5])
   * @returns A computed rating number
   */
  function getRating(value?: string, precision: RatingPrecision = 1, range?: RatingRange): number {
    if (!value) {
      return 0
    }

    const [min = MIN_RATING, max = MAX_RATING] = range || []
    const random = new FastRandom(fastHash(value))
    return +(random.next() * (max - min) + min).toFixed(precision)
  }

  return { getRating, MIN_RATING, MAX_RATING }
}
